import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/api/queryClient";
import {
  stationDirectoryQuery,
  usersKeys,
} from "./usersQueries";

interface NameLike {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

function userToName(u: NameLike): string {
  const full = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return full || u.email || "";
}

/**
 * Synchronous name lookup backed by the react-query cache (previously a
 * module-level Map). Called both at CO-comment decode time and at render
 * time — must stay synchronous. Scans every cached user list, handling both
 * the directory shape ({ users }) and the roster shape (ReportGenUser[]).
 */
export function getUserName(userId: string): string | undefined {
  const entries = queryClient.getQueriesData<unknown>({ queryKey: usersKeys.all });
  for (const [, data] of entries) {
    if (!data) continue;
    const list = Array.isArray(data)
      ? (data as NameLike[])
      : (data as { users?: NameLike[] }).users;
    if (!Array.isArray(list)) continue;
    const user = list.find((u) => u?.id === userId);
    if (user) {
      const name = userToName(user);
      if (name) return name;
    }
  }
  return undefined;
}

/**
 * Warms the station directory in the query cache so getUserName resolves.
 * Same contract as the old Map-based implementation: `ready` flips once the
 * load settles (success OR failure — name resolution is non-critical and
 * consumers fall back to the raw userId).
 */
export function useUserDirectory(stationId: string | undefined): {
  resolveName: (userId: string) => string | undefined;
  ready: boolean;
} {
  const query = useQuery({
    ...stationDirectoryQuery(stationId ?? ""),
    enabled: !!stationId,
  });

  return {
    resolveName: getUserName,
    ready: query.isSuccess || query.isError,
  };
}
